import { Grid } from "@mui/material";
import { hasAnyAccessToResource } from "api/general";
import { useSegments } from "api/segments";
import { DateInput } from "components/FormControls/DateInput";
import { DateTimeInput } from "components/FormControls/DateTimeInput";
import { FormSection } from "components/FormControls/FormSection";
import { SelectInput } from "components/FormControls/Select/Select";
import { TextInput } from "components/FormControls/TextInput";
import { TierSetDropdown } from "components/FormControls/TierSetDropdown/TierSetDropdown";
import { ReactComponent as HelpCircleIcon } from "components/Icons/help-circle.svg";
import { Tooltip } from "components/Tooltips/Tooltip";
import { genderList } from "constants/general";
import { Field } from "formik";
import { useMessage } from "hooks/intl";
import { useManageInfiniteScroll } from "hooks/useManageInfiniteScroll";
import { useState } from "react";
import { useStyles } from "../SimulateCampaign.styles";
import { MemberId } from "./MemberId";

export type MemberDetailsTypes = {
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
  birthDate?: string;
  levelId?: string;
  levelAchievementDate?: string;
  gender?: string;
  registeredDate?: string;
  segments?: string[];
  loyaltyCardNumber?: string;
  id?: string;
};

interface Props {
  fieldNamePrefix: "customer" | "referrer";
  setCustomerValues: (memberDetails: MemberDetailsTypes) => void;
  arePreloadedData: boolean;
}

export const PersonDetails = ({
  fieldNamePrefix,
  setCustomerValues,
  arePreloadedData,
}: Props) => {
  const classes = useStyles();
  const { message } = useMessage();
  const hasAnyTierPermission = hasAnyAccessToResource("LEVEL");

  const isCustomerForm = fieldNamePrefix === "customer";
  const formSectionTitle = (
    <span className={classes.titleWrapper}>
      {message({
        id: isCustomerForm ? "memberDetails" : "referrerDetails",
      })}
      {isCustomerForm && (
        <Tooltip
          text={message({ id: "simulateCampaignMemberSectionDescription" })}
        >
          <HelpCircleIcon className={classes.icon} />
        </Tooltip>
      )}
    </span>
  );
  const dynamicXs = hasAnyTierPermission ? 12 : 6;

  const [segmentSearch, setSegmentSearch] = useState("");

  const { useInfiniteSegmentsList } = useSegments();

  const {
    data: infiniteSegmentsList,
    size: segmentsSize,
    setSize: setSegmentsSize,
    isValidating: isSegmentsListValidating,
  } = useInfiniteSegmentsList({
    filters: segmentSearch
      ? [
          {
            condition: "like",
            query: "name",
            value: segmentSearch,
          },
          {
            query: "active",
            condition: "eq",
            value: "true",
          },
        ]
      : [
          {
            query: "active",
            condition: "eq",
            value: "true",
          },
        ],
    order: "asc",
    sortKey: "name",
  });

  const { infiniteData: segmentsList, anyElementsToFetch: anySegmentsToFetch } =
    useManageInfiniteScroll(infiniteSegmentsList || []);

  return (
    <FormSection title={formSectionTitle}>
      <Grid container spacing={12}>
        <Grid item xs={6}>
          <TextInput
            className={classes.input}
            label={message({ id: "firstName" })}
            name={`${fieldNamePrefix}.firstName`}
            type="text"
            data-test-id="first-name"
            disabled={arePreloadedData}
            width="100%"
          />
        </Grid>
        <Grid item xs={6}>
          <TextInput
            className={classes.input}
            label={message({ id: "lastName" })}
            name={`${fieldNamePrefix}.lastName`}
            type="text"
            data-test-id="last-name"
            disabled={arePreloadedData}
            width="100%"
          />
        </Grid>
        <Grid item xs={6}>
          <TextInput
            className={classes.input}
            label={message({ id: "phoneNumber" })}
            name={`${fieldNamePrefix}.phone`}
            type="text"
            data-test-id="phone-number"
            disabled={arePreloadedData}
            width="100%"
          />
        </Grid>
        <Grid item xs={6}>
          <TextInput
            className={classes.input}
            label={message({ id: "email" })}
            name={`${fieldNamePrefix}.email`}
            type="text"
            data-test-id="email"
            disabled={arePreloadedData}
            width="100%"
          />
        </Grid>
        <Grid item xs={6}>
          <TextInput
            className={classes.input}
            label={message({ id: "loyaltyCardNumber" })}
            name={`${fieldNamePrefix}.loyaltyCardNumber`}
            type="text"
            data-test-id="loyalty-card-number"
            disabled={arePreloadedData}
            width="100%"
          />
        </Grid>
        <Grid item xs={6}>
          <DateInput
            className={classes.input}
            defaultValue={null}
            label={message({ id: "dateOfBirth" })}
            name={`${fieldNamePrefix}.birthDate`}
            data-test-id="date-of-birth"
            disabled={arePreloadedData}
            fullWidth
          />
        </Grid>
        {hasAnyTierPermission ? (
          <Grid item xs={6}>
            <TierSetDropdown
              name={`${fieldNamePrefix}.tiers`}
              label={message({ id: "tiers" })}
            />
          </Grid>
        ) : null}
        <Grid item xs={6}>
          <DateTimeInput
            className={classes.input}
            defaultValue={null}
            format="yyyy-MM-dd HH:mmXXX"
            label={message({ id: "tierPromotionDate" })}
            name={`${fieldNamePrefix}.levelAchievementDate`}
            data-test-id="tier-promotion-date"
            disabled={arePreloadedData}
            width="100%"
          />
        </Grid>
        <Grid item xs={6}>
          <SelectInput
            items={
              genderList.map((item: string) => ({
                id: item,
                label: message({ id: item }),
              })) || []
            }
            label={message({ id: "gender" })}
            name={`${fieldNamePrefix}.gender`}
            data-test-id="gender"
            disabled={arePreloadedData}
            isClearable
          />
        </Grid>
        <Grid item xs={6}>
          <DateTimeInput
            className={classes.input}
            defaultValue={null}
            format="yyyy-MM-dd HH:mmXXX"
            label={message({ id: "registrationDate" })}
            name={`${fieldNamePrefix}.registeredDate`}
            data-test-id="registration-date"
            disabled={arePreloadedData}
            width="100%"
          />
        </Grid>
        <Grid item xs={dynamicXs}>
          <SelectInput
            name={`${fieldNamePrefix}.segments`}
            multiple
            displaySearch
            items={segmentsList.map((segment) => ({
              label: segment.name,
              id: segment.segmentId,
            }))}
            label={message({ id: "segment" })}
            placeholder={message({
              id: "segment",
            })}
            asyncSearch={(value) => {
              setSegmentSearch(value);
            }}
            data-test-id="segment"
            disabled={arePreloadedData}
            anyElementsToFetch={anySegmentsToFetch}
            loadMoreElements={() =>
              !isSegmentsListValidating && setSegmentsSize(segmentsSize + 1)
            }
            isClearable
          />
        </Grid>
        <Grid item xs={dynamicXs}>
          <Field name={fieldNamePrefix}>
            {({ field }: { field: any }) => {
              return (
                <MemberId
                  name={fieldNamePrefix}
                  setCustomerValues={setCustomerValues}
                  initialMemberId={field.value.id}
                />
              );
            }}
          </Field>
        </Grid>
      </Grid>
    </FormSection>
  );
};
